import { useState } from 'react';
import { Button, Card, Col, Empty, Form, Input, InputNumber, Modal, Row, Select, Space, Statistic, Tabs, Tag, Typography, App as AntApp, Skeleton } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProject } from '../api/projects';
import { useBlocksForProject, useCreateBlock, useDeleteBlock } from '@/features/blocks/api/blocks';
import {
  useApartmentGrid,
  useBulkCreateApartments,
  type ApartmentLayout,
  type GridBlock,
} from '@/features/apartments/api/apartments';
import { ApartmentGrid } from '@/features/apartments/components/apartment-grid';
import { ApartmentDrawer } from '@/features/apartments/components/apartment-drawer';
import { PricingPlansTab } from '@/features/pricing-plans/components/pricing-plans-tab';
import { asApiError } from '@/shared/lib/api';
import { formatUzs } from '@/shared/lib/format';

const LAYOUTS: ApartmentLayout[] = ['studio', 'one_room', 'two_room', 'three_room', 'four_room_plus'];

function SectionTitle({ step, title }: { step: number; title: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 12px' }}>
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: 4,
          background: '#f1f5f9',
          color: '#475569',
          fontSize: 11,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {step}
      </span>
      <Typography.Text
        strong
        style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}
      >
        {title}
      </Typography.Text>
    </div>
  );
}

export function ProjectDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading } = useProject(id);
  const { data: grid } = useApartmentGrid(id);
  const { data: blocks } = useBlocksForProject(id);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [blockOpen, setBlockOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const { modal, message } = AntApp.useApp();
  const deleteBlock = useDeleteBlock(id ?? '');

  if (isLoading) return <Skeleton active />;
  if (!project) return <Empty />;

  const counts = grid?.counts ?? {};
  const total = grid?.totalApartments ?? 0;
  const sold = (counts.contracted ?? 0) + (counts.sold ?? 0) + (counts.delivered ?? 0);
  const soldPct = total ? Math.round((sold / total) * 100) : 0;

  function handleDeleteBlock(block: GridBlock) {
    const count = block.floors.reduce((sum, f) => sum + f.units.length, 0);
    modal.confirm({
      title: t('blocks.deleteConfirmTitle'),
      content: t('blocks.deleteConfirmBody', { name: block.name, count }),
      okText: t('blocks.delete'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await deleteBlock.mutateAsync(block.id);
          void message.success(t('blocks.deleted'));
        } catch (err) {
          void message.error(asApiError(err).message);
          throw err; // keep the dialog open on failure
        }
      },
    });
  }

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <div>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/projects')}>
          {t('projects.title')}
        </Button>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 8 }}>
          <Space size={12} align="baseline">
            <Typography.Title level={2} style={{ margin: 0 }}>{project.name}</Typography.Title>
            <Typography.Text code style={{ fontSize: 13 }}>{project.code}</Typography.Text>
            <Tag>{t(`projects.status.${project.status}`)}</Tag>
          </Space>
        </div>
        <Typography.Text type="secondary">{project.address}</Typography.Text>
      </div>

      <Row gutter={16}>
        <Col xs={12} md={6}>
          <Card><Statistic title={t('apartments.total')} value={total} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title={t('apartments.available')} value={counts.available ?? 0} valueStyle={{ color: '#16a34a' }} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title={t('apartments.reserved')} value={counts.reserved ?? 0} valueStyle={{ color: '#eab308' }} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title={t('apartments.soldPct')} value={`${soldPct}%`} /></Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="inventory"
        items={[
          {
            key: 'inventory',
            label: t('projects.tabs.inventory'),
            children: (
              <Card
                title={t('apartments.gridTitle')}
                extra={
                  <Space>
                    <Button onClick={() => setBlockOpen(true)} icon={<PlusOutlined />}>
                      {t('blocks.new')}
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setBulkOpen(true)}
                      disabled={!(blocks?.length ?? 0)}
                    >
                      {t('apartments.bulkNew')}
                    </Button>
                  </Space>
                }
              >
                {grid ? (
                  <ApartmentGrid grid={grid} onSelectUnit={setDrawerId} onDeleteBlock={handleDeleteBlock} />
                ) : (
                  <Skeleton active />
                )}
              </Card>
            ),
          },
          {
            key: 'pricing',
            label: t('projects.tabs.pricing'),
            children: <PricingPlansTab projectId={project.id} />,
          },
        ]}
      />

      <ApartmentDrawer apartmentId={drawerId} projectId={project.id} onClose={() => setDrawerId(null)} />
      <NewBlockModal open={blockOpen} projectId={project.id} onClose={() => setBlockOpen(false)} />
      <BulkApartmentsModal
        open={bulkOpen}
        projectId={project.id}
        blocks={blocks ?? []}
        onClose={() => setBulkOpen(false)}
      />
    </Space>
  );
}

function NewBlockModal({ open, projectId, onClose }: { open: boolean; projectId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const create = useCreateBlock(projectId);
  const [form] = Form.useForm();

  async function onOk() {
    try {
      const values = await form.validateFields();
      await create.mutateAsync(values);
      void message.success(t('blocks.created'));
      form.resetFields();
      onClose();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Modal open={open} onCancel={onClose} onOk={onOk} title={t('blocks.newTitle')} confirmLoading={create.isPending} okText={t('common.create')} cancelText={t('common.cancel')} destroyOnHidden>
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ position: 0 }}>
        <Form.Item name="code" label={t('blocks.code')} rules={[{ required: true, pattern: /^[A-Z0-9]+$/i }]}>
          <Input placeholder="A" maxLength={8} />
        </Form.Item>
        <Form.Item name="name" label={t('blocks.name')} rules={[{ required: true }]}>
          <Input placeholder="Block A" />
        </Form.Item>
        <Form.Item name="position" label={t('blocks.position')}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

type GroupFormValue = {
  floorFrom: number;
  floorTo: number;
  unitsPerFloor: number;
  pricePerSqm: number; // raw UZS per m² (no tiyin)
  unitTemplates: Array<{ layoutType: ApartmentLayout; areaSqm: number; bathrooms: number; balconies: number }>;
};

type BulkFormValues = {
  blockId: string;
  numberingPattern: 'per_floor_reset' | 'continuous';
  groups: GroupFormValue[];
};

const EMPTY_TEMPLATE = { layoutType: 'one_room' as ApartmentLayout, areaSqm: 50, bathrooms: 1, balconies: 0 };

// Layout already implies room count — don't ask the user twice.
// `four_room_plus` defaults to 4 (admin can bump per-unit after create).
function roomsFor(layout: ApartmentLayout): number {
  switch (layout) {
    case 'studio': return 1;
    case 'one_room': return 1;
    case 'two_room': return 2;
    case 'three_room': return 3;
    case 'four_room_plus': return 4;
    case 'penthouse': return 4;
    case 'commercial': return 0;
  }
}

function ColLabel({ text }: { text: string }) {
  return (
    <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {text}
    </Typography.Text>
  );
}

function defaultGroup(): GroupFormValue {
  return {
    floorFrom: 1,
    floorTo: 1,
    unitsPerFloor: 3,
    pricePerSqm: 9_400_000,
    unitTemplates: [{ ...EMPTY_TEMPLATE }, { ...EMPTY_TEMPLATE }, { ...EMPTY_TEMPLATE }],
  };
}

function computeCoverageError(groups: GroupFormValue[]): { floor: number; kind: 'gap' | 'overlap' } | null {
  if (!groups || groups.length === 0) return null;
  const valid = groups.filter((g) => Number.isFinite(g?.floorFrom) && Number.isFinite(g?.floorTo));
  if (valid.length === 0) return null;
  const min = Math.min(...valid.map((g) => Math.min(g.floorFrom, g.floorTo)));
  const max = Math.max(...valid.map((g) => Math.max(g.floorFrom, g.floorTo)));
  const cover = new Map<number, number>();
  for (const g of valid) {
    const lo = Math.min(g.floorFrom, g.floorTo);
    const hi = Math.max(g.floorFrom, g.floorTo);
    for (let f = lo; f <= hi; f++) {
      if (f === 0) continue;
      cover.set(f, (cover.get(f) ?? 0) + 1);
    }
  }
  for (let f = min; f <= max; f++) {
    if (f === 0) continue;
    const c = cover.get(f) ?? 0;
    if (c === 0) return { floor: f, kind: 'gap' };
    if (c > 1) return { floor: f, kind: 'overlap' };
  }
  return null;
}

function BulkApartmentsModal({
  open,
  projectId,
  blocks,
  onClose,
}: {
  open: boolean;
  projectId: string;
  blocks: Array<{ id: string; code: string; name: string }>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const bulk = useBulkCreateApartments(projectId);
  const [form] = Form.useForm<BulkFormValues>();
  const watchGroups = Form.useWatch('groups', form);
  const coverageError = computeCoverageError((watchGroups ?? []) as GroupFormValue[]);

  async function onOk() {
    if (coverageError) {
      void message.error(
        t(
          coverageError.kind === 'gap'
            ? 'apartments.coverageGapError'
            : 'apartments.coverageOverlapError',
          { floor: coverageError.floor },
        ),
      );
      return;
    }
    try {
      const values = await form.validateFields();
      const result = await bulk.mutateAsync({
        blockId: values.blockId,
        numberingPattern: values.numberingPattern,
        groups: values.groups.map((g) => ({
          floors: { from: Number(g.floorFrom), to: Number(g.floorTo) },
          unitsPerFloor: Number(g.unitsPerFloor),
          pricePerSqm: String(BigInt(Math.round(Number(g.pricePerSqm) * 100))),
          unitTemplates: g.unitTemplates.map((tpl, i) => ({
            unitIndex: i + 1,
            layoutType: tpl.layoutType,
            areaSqm: Number(tpl.areaSqm),
            rooms: roomsFor(tpl.layoutType),
            bathrooms: Number(tpl.bathrooms ?? 1),
            balconies: Number(tpl.balconies ?? 0),
          })),
        })),
      });
      void message.success(`${result.apartmentsCreated} ${t('apartments.createdPlural')}`);
      form.resetFields();
      onClose();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onOk}
      title={t('apartments.bulkTitle')}
      width={960}
      style={{ top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingRight: 8 } }}
      confirmLoading={bulk.isPending}
      okText={t('apartments.bulkCreate')}
      okButtonProps={{ disabled: !!coverageError }}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        size="middle"
        initialValues={{
          numberingPattern: 'continuous',
          groups: [defaultGroup()],
        }}
      >
        <SectionTitle step={1} title={t('apartments.bulkStepLayout')} />
        <Row gutter={16}>
          <Col xs={24} md={14}>
            <Form.Item name="blockId" label={t('blocks.title')} rules={[{ required: true }]}>
              <Select
                placeholder={t('blocks.title')}
                options={blocks.map((b) => ({ value: b.id, label: `${b.code} · ${b.name}` }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item
              name="numberingPattern"
              label={t('apartments.numbering')}
              tooltip={t('apartments.numberingHint')}
            >
              <Select
                options={[
                  { value: 'per_floor_reset', label: t('apartments.numberingResetPerFloor') },
                  { value: 'continuous', label: t('apartments.numberingContinuous') },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle step={2} title={t('apartments.floorGroups')} />
        <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 16 }}>
          {t('apartments.floorGroupsIntro')}
        </Typography.Paragraph>

        <Form.List name="groups">
          {(groupFields, { add: addGroup, remove: removeGroup }) => (
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
              {groupFields.map((groupField, gi) => (
                <FloorGroupCard
                  key={groupField.key}
                  form={form}
                  groupField={groupField}
                  gi={gi}
                  canRemove={groupFields.length > 1}
                  onRemove={() => removeGroup(groupField.name)}
                />
              ))}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => addGroup(defaultGroup())}
                block
              >
                {t('apartments.floorGroupAdd')}
              </Button>
            </Space>
          )}
        </Form.List>

        {coverageError && (
          <div
            style={{
              marginTop: 12,
              padding: '8px 12px',
              borderRadius: 6,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#b91c1c',
              fontSize: 13,
            }}
          >
            {t(
              coverageError.kind === 'gap'
                ? 'apartments.coverageGapError'
                : 'apartments.coverageOverlapError',
              { floor: coverageError.floor },
            )}
          </div>
        )}

        <SectionTitle step={3} title={t('apartments.pricePreview')} />
        <PricePreview groups={(watchGroups ?? []) as GroupFormValue[]} />
      </Form>
    </Modal>
  );
}

function FloorGroupCard({
  form,
  groupField,
  gi,
  canRemove,
  onRemove,
}: {
  form: ReturnType<typeof Form.useForm<BulkFormValues>>[0];
  groupField: { name: number; key: number };
  gi: number;
  canRemove: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const unitsPerFloor = (Form.useWatch(['groups', groupField.name, 'unitsPerFloor'], form) ?? 1) as number;
  const watchedTemplates = (Form.useWatch(['groups', groupField.name, 'unitTemplates'], form) ?? []) as Array<{
    layoutType: ApartmentLayout;
    areaSqm: number;
  }>;

  // Auto-sync template count to unitsPerFloor (grow with defaults, shrink by chopping the tail).
  if (Array.isArray(watchedTemplates) && watchedTemplates.length !== unitsPerFloor) {
    queueMicrotask(() => {
      const next = [...watchedTemplates];
      while (next.length < unitsPerFloor) next.push({ ...EMPTY_TEMPLATE });
      while (next.length > unitsPerFloor) next.pop();
      form.setFieldValue(['groups', groupField.name, 'unitTemplates'], next);
    });
  }

  return (
    <Card
      size="small"
      styles={{ body: { padding: 16 } }}
      title={
        <Typography.Text strong style={{ fontSize: 13 }}>
          {t('apartments.floorGroupTitle', { n: gi + 1 })}
        </Typography.Text>
      }
      extra={
        canRemove && (
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={onRemove}>
            {t('apartments.floorGroupRemove')}
          </Button>
        )
      }
    >
      <Row gutter={12}>
        <Col xs={12} md={5}>
          <Form.Item
            name={[groupField.name, 'floorFrom']}
            label={t('apartments.floorFrom')}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} min={-5} max={200} />
          </Form.Item>
        </Col>
        <Col xs={12} md={5}>
          <Form.Item
            name={[groupField.name, 'floorTo']}
            label={t('apartments.floorTo')}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} min={-5} max={200} />
          </Form.Item>
        </Col>
        <Col xs={12} md={5}>
          <Form.Item
            name={[groupField.name, 'unitsPerFloor']}
            label={t('apartments.unitsPerFloor')}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} min={1} max={20} />
          </Form.Item>
        </Col>
        <Col xs={24} md={9}>
          <Form.Item
            name={[groupField.name, 'pricePerSqm']}
            label={t('apartments.pricePerSqm')}
            rules={[{ required: true }]}
            tooltip={t('apartments.pricePerSqmHint')}
          >
            <InputNumber<number>
              style={{ width: '100%' }}
              min={0}
              step={100000}
              formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={(v) => Number((v ?? '').replace(/\s/g, '')) as number}
              addonAfter="UZS/м²"
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.List name={[groupField.name, 'unitTemplates']}>
        {(unitFields) => (
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {/* Column headers — desktop only (md+) */}
            <Row gutter={8} style={{ padding: '0 8px' }}>
              <Col xs={0} md={4} />
              <Col xs={0} md={8}><ColLabel text={t('apartments.layout_')} /></Col>
              <Col xs={0} md={4}><ColLabel text={t('apartments.area')} /></Col>
              <Col xs={0} md={4}><ColLabel text={t('apartments.bathrooms')} /></Col>
              <Col xs={0} md={4}><ColLabel text={t('apartments.balconies')} /></Col>
            </Row>

            {unitFields.map((unitField, ui) => (
              <Row
                key={unitField.key}
                gutter={[8, 8]}
                align="middle"
                style={{ background: '#f8fafc', padding: 8, borderRadius: 6 }}
              >
                <Col xs={24} md={4}>
                  <Typography.Text style={{ fontSize: 12, color: '#475569' }}>
                    {t('apartments.unitTemplate', { n: ui + 1 })}
                  </Typography.Text>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={[unitField.name, 'layoutType']}
                    rules={[{ required: true }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      placeholder={t('apartments.layout_')}
                      options={LAYOUTS.map((l) => ({ value: l, label: t(`apartments.layout.${l}`) }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={8} md={4}>
                  <Form.Item
                    name={[unitField.name, 'areaSqm']}
                    rules={[{ required: true }]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={500}
                      addonAfter="м²"
                      aria-label={t('apartments.area')}
                    />
                  </Form.Item>
                </Col>
                <Col xs={8} md={4}>
                  <Form.Item
                    name={[unitField.name, 'bathrooms']}
                    rules={[{ required: true }]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={10}
                      aria-label={t('apartments.bathrooms')}
                    />
                  </Form.Item>
                </Col>
                <Col xs={8} md={4}>
                  <Form.Item
                    name={[unitField.name, 'balconies']}
                    rules={[{ required: true }]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={10}
                      aria-label={t('apartments.balconies')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            ))}
          </Space>
        )}
      </Form.List>
    </Card>
  );
}

function PricePreview({ groups }: { groups: GroupFormValue[] }) {
  const { t } = useTranslation();
  const rows: string[] = [];
  let total = 0n;
  let count = 0;

  for (const g of groups ?? []) {
    if (!g || !Number.isFinite(g.floorFrom) || !Number.isFinite(g.floorTo) || !Number.isFinite(g.pricePerSqm)) continue;
    const lo = Math.min(g.floorFrom, g.floorTo);
    const hi = Math.max(g.floorFrom, g.floorTo);
    let floorCount = hi - lo + 1;
    if (lo <= 0 && hi >= 0) floorCount = Math.max(0, floorCount - 1); // floor 0 skipped
    const pricePerSqmTiyin = BigInt(Math.round((g.pricePerSqm || 0) * 100));
    for (const tpl of g.unitTemplates ?? []) {
      if (!tpl || !Number.isFinite(tpl.areaSqm)) continue;
      const apartmentPrice = (BigInt(Math.round(tpl.areaSqm * 100)) * pricePerSqmTiyin) / 100n;
      total += apartmentPrice * BigInt(floorCount);
      count += floorCount;
    }
    const sampleTpl = g.unitTemplates?.[0];
    if (sampleTpl && Number.isFinite(sampleTpl.areaSqm)) {
      const sample = (BigInt(Math.round(sampleTpl.areaSqm * 100)) * pricePerSqmTiyin) / 100n;
      rows.push(
        t('apartments.pricePreviewRow', {
          floors: lo === hi ? `${lo}` : `${lo}–${hi}`,
          area: sampleTpl.areaSqm,
          price: formatUzs(pricePerSqmTiyin) + '/м²',
          total: formatUzs(sample),
        }),
      );
    }
  }

  if (rows.length === 0) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        —
      </Typography.Text>
    );
  }

  return (
    <div style={{ background: '#f1f5f9', padding: 12, borderRadius: 8 }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        {rows.map((r, i) => (
          <Typography.Text key={i} style={{ fontSize: 13, color: '#0f172a' }}>
            {r}
          </Typography.Text>
        ))}
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px dashed #cbd5e1',
            fontSize: 13,
          }}
        >
          <Typography.Text strong style={{ color: '#0f172a' }}>
            {count} ед. · {formatUzs(total)}
          </Typography.Text>
        </div>
      </Space>
    </div>
  );
}
